import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { AgentConfig } from './agentConfigs';
import type { Daemon, QueueRunnerState, SharedDaemon } from './daemon';
import type { Team } from './teams';

export type MeFeedEntityType =
  | 'PLAN'
  | 'COMPLETION_REPORT'
  | 'POST_MORTEM'
  | 'CO_ACTION'
  | 'CODE_REVIEW'
  | 'DOCUMENT'
  | 'CONVENTION'
  | 'SKILL';
export type HomeActivityEntityType = MeFeedEntityType;

export type MeFeedItem = {
  id: string;
  entityType: MeFeedEntityType;
  title: string;
  status: string;
  sessionStatus: import('./coActions').CoActionSessionStatus | null;
  qualityScore: number | null;
  projectId: string;
  projectName: string | null;
  teamId: string | null;
  createdByName: string | null;
  occurredAt: string;
  archivedAt: string | null;
  // PLAN 항목에만 채워지는 plan 전용 속성(우선순위/유형). 그 외 엔티티는 null.
  priority: string | null;
  type: string | null;
  // PLAN 항목에 연결된 결과물 요약(보고서/포스트모템). 그 외 엔티티는 null/0.
  linkedReportId: string | null;
  linkedReportCount: number;
  linkedReportQualityScore: number | null;
  linkedReportReviewRecommendation: string | null;
  linkedReportReviewReason: string | null;
  linkedReportReviewStatus: string | null;
  linkedPostMortemId: string | null;
  linkedPostMortemCount: number;
  // PLAN 항목에 연결된(sourcePlanId) 코드리뷰 요약. 그 외 엔티티는 null/0.
  linkedCodeReviewId: string | null;
  linkedCodeReviewCount: number;
};

export type MePagedFeed = {
  data: MeFeedItem[];
  nextCursor: string | null;
};

export type MeActionRequiredPreview = {
  id: string;
  projectId: string;
  projectName: string | null;
  teamId: string | null;
  title: string;
  status: string;
  occurredAt: string;
};

export type MeActionRequiredCategory = {
  total: number;
  recent: MeActionRequiredPreview[];
};

export type MeActionRequired = {
  pendingPlans: MeActionRequiredCategory;
  failedReports: MeActionRequiredCategory;
  openPostMortems: MeActionRequiredCategory;
  reviewNeededReports: MeActionRequiredCategory;
  byProjectId: Record<string, number>;
};

export type MeProjectEntry = {
  id: string;
  teamId: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  lastInteractedAt: string | null;
};

export type MeTeamWithProjects = {
  team: { id: string; name: string };
  projects: MeProjectEntry[];
};

export type MeFeedAuthor = 'me' | 'others' | 'agents' | 'all';

export type MeFeedFilter = {
  entity?: MeFeedEntityType[];
  author?: MeFeedAuthor;
  planStatus?: string[];
  reportStatus?: string[];
  postMortemStatus?: string[];
  coActionStatus?: string[];
  codeReviewStatus?: string[];
  projectIds?: string[];
};

export type MeActiveRun = {
  id: string;
  status: string;
  runnerType: string;
  model: string | null;
  agentConfigId: string;
  agentConfigName: string | null;
  projectId: string;
  projectName: string | null;
  teamId: string | null;
  targetDaemonId: string | null;
  claimedByDaemonId: string | null;
  daemonName: string | null;
  daemonOsType: string | null;
  promptPreview: string;
  planMode: boolean;
  useWorktree: boolean;
  startedAt: string | null;
  lastHeartbeatAt: string | null;
  createdAt: string;
  updatedAt: string;
  chainId: string | null;
  parentTriggerId: string | null;
  chainKind: 'RETRY' | 'PLAN_TASK' | null;
  chainPlanId: string | null;
  chainPlanTitle: string | null;
  chainTaskDoneCount: number | null;
  chainTaskTotalCount: number | null;
  chainCurrentTaskTitle: string | null;
  chainAttemptCount: number | null;
  // 러너가 히스토리에 남긴 질문이 있는지. 서버가 판정만 내려주고 원문은 오지 않는다.
  hasQuestionsForUser: boolean;
  // 소유 러너 PENDING 대기열 순번(1부터). 종료 상태·공유 러너·미지정 요청은 null.
  queuePosition: number | null;
  // 순번이 붙은 요청의 대상 러너 상태. 순번이 없으면 null.
  queueRunnerState: QueueRunnerState | null;
};

type MeActionRequiredResponse = { data: MeActionRequired };
type MeProjectsResponse = { data: MeTeamWithProjects[] };
type MeActiveRunsResponse = { data: MeActiveRun[] };

export type MeHomeBootstrap = {
  projects: MeTeamWithProjects[];
  teams: Team[];
  daemons: Daemon[];
  onlineThresholdMs: number;
  sharedDaemonsByProjectId: Record<string, SharedDaemon[]>;
  agentConfigsByProjectId: Record<string, AgentConfig[]>;
  feed: MePagedFeed;
  recentRuns: {
    active: MeActiveRun[];
    past: MeActiveRun[];
  };
  heatmap: MeActivityHeatmap;
};

export type MeHomeBootstrapOptions = {
  limit?: number;
  entity?: MeFeedEntityType[];
  author?: MeFeedAuthor;
  planStatus?: string[];
  reportStatus?: string[];
  postMortemStatus?: string[];
  coActionStatus?: string[];
  codeReviewStatus?: string[];
  projectIds?: string[];
  days?: number;
  tzOffsetMinutes?: number;
};

type MeHomeResponse = { data: MeHomeBootstrap };

const appendArrayParam = (params: URLSearchParams, key: string, values?: string[]) => {
  if (values && values.length > 0) {
    params.set(key, values.join(','));
  }
};

export const fetchMeHome = async (token: string, opts: MeHomeBootstrapOptions = {}): Promise<MeHomeBootstrap> => {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.author) params.set('author', opts.author);
  if (typeof opts.days === 'number') params.set('days', String(opts.days));
  if (typeof opts.tzOffsetMinutes === 'number') {
    params.set('tzOffsetMinutes', String(opts.tzOffsetMinutes));
  }
  appendArrayParam(params, 'entity', opts.entity);
  appendArrayParam(params, 'planStatus', opts.planStatus);
  appendArrayParam(params, 'reportStatus', opts.reportStatus);
  appendArrayParam(params, 'postMortemStatus', opts.postMortemStatus);
  appendArrayParam(params, 'coActionStatus', opts.coActionStatus);
  appendArrayParam(params, 'codeReviewStatus', opts.codeReviewStatus);
  appendArrayParam(params, 'projectIds', opts.projectIds);

  const query = params.toString();
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/home${query ? `?${query}` : ''}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch home bootstrap: ${response.status}`);
  }

  const body = (await response.json()) as MeHomeResponse;
  return body.data;
};

export type MeRecentRunsGroup = 'active' | 'past' | 'all';

export const fetchMeRecentRuns = async (
  token: string,
  group: MeRecentRunsGroup = 'all',
  projectIds?: string[],
  opts: { isFavorite?: boolean } = {},
): Promise<MeActiveRun[]> => {
  const params = new URLSearchParams();
  if (group !== 'all') params.set('group', group);
  if (projectIds && projectIds.length > 0) params.set('projectIds', projectIds.join(','));
  if (opts.isFavorite === true) params.set('isFavorite', 'true');
  const queryString = params.toString();
  const query = queryString.length > 0 ? `?${queryString}` : '';
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/recent-runs${query}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch recent runs: ${response.status}`);
  }

  const body = (await response.json()) as MeActiveRunsResponse;
  return body.data;
};

export const fetchMeProjects = async (token: string): Promise<MeTeamWithProjects[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/projects`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch me projects: ${response.status}`);
  }

  const body = (await response.json()) as MeProjectsResponse;
  return body.data;
};

/** 내가 받은 대기 중 초대. 수락 토큰은 응답에 없으며, 수락은 메일 링크로만 가능하다. */
export type ReceivedInvitation = {
  id: string;
  type: string;
  teamId: string | null;
  projectId: string | null;
  teamName: string | null;
  projectName: string | null;
  projectRoleName: string | null;
  role: string;
  expiresAt: string;
  createdAt: string;
};

export const fetchMyInvitations = async (token: string): Promise<ReceivedInvitation[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/invitations`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch invitations: ${response.status}`);
  }

  const body = (await response.json()) as { data: ReceivedInvitation[] };
  return body.data;
};

export const touchProject = async (token: string | null, projectId: string): Promise<void> => {
  if (!token || projectId.length === 0) return;

  try {
    const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/projects/${projectId}/touch`, {
      method: 'POST',
      cache: 'no-store',
      headers: authHeaders(token),
    });

    if (!response.ok) {
      console.debug(`Failed to touch project: ${response.status}`);
    }
  } catch (error) {
    console.debug('Failed to touch project', error);
  }
};

export type MeActivityHeatmapBucket = {
  date: string;
  count: number;
};

export type MeActivityHeatmap = {
  buckets: MeActivityHeatmapBucket[];
};

type MeActivityHeatmapResponse = { data: MeActivityHeatmap };

export const fetchMeActivityHeatmap = async (
  token: string,
  days: number,
  tzOffsetMinutes: number,
  projectIds?: string[],
): Promise<MeActivityHeatmap> => {
  const params = new URLSearchParams({
    days: String(days),
    tzOffsetMinutes: String(tzOffsetMinutes),
  });
  if (projectIds && projectIds.length > 0) params.set('projectIds', projectIds.join(','));
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/activity-heatmap?${params.toString()}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch activity-heatmap: ${response.status}`);
  }

  const body = (await response.json()) as MeActivityHeatmapResponse;
  return body.data;
};

export const fetchMeActionRequired = async (token: string, projectIds?: string[]): Promise<MeActionRequired> => {
  const params = new URLSearchParams();
  if (projectIds && projectIds.length > 0) params.set('projectIds', projectIds.join(','));
  const queryString = params.toString();
  const url = `${getApiUrl()}/api/me/action-required${queryString.length > 0 ? `?${queryString}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch action-required: ${response.status}`);
  }

  const body = (await response.json()) as MeActionRequiredResponse;
  return body.data;
};
